import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import ActionRowMenu from 'components/IAP/ActionRowMenu';
import { AssigneeCell } from 'components/IAP/actionsTable/AssigneeCell';
import { DueDateCell } from 'components/IAP/actionsTable/DueDateCell';
import { PriorityCell } from 'components/IAP/actionsTable/PriorityCell';
import { StatusCell } from 'components/IAP/actionsTable/StatusCell';
import { actionPriorityRank } from 'components/IAP/actionsTable/actionEditModel';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import { useActionAssigneeDirectory } from 'components/IAP/hooks/useActionAssigneeDirectory';
import { useAors } from 'hooks/useAors';
import type { PlanLibraryTask } from 'hooks/planLibrary/planLibraryHooks';
import { planTaskToActionEntity } from './planLibraryTaskModel';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';

function actionLocationLabel(action: ActionEntity): string {
  const geolocation = (
    action as ActionEntity & {
      geolocation?: { address?: string; name?: string } | null;
    }
  ).geolocation;
  return (
    geolocation?.address ||
    geolocation?.name ||
    (geolocation ? 'Map location set' : '—')
  );
}

export function PlanTaskTable({
  tasks,
  onEdit,
  onDelete,
}: {
  tasks: PlanLibraryTask[];
  onEdit: (task: PlanLibraryTask) => void;
  onDelete: (taskId: string) => void;
}) {
  const { data: aors = [] } = useAors();
  const directory = useActionAssigneeDirectory(ORGANIZATION);
  const assigneeDirectory = useMemo(() => {
    const titles = tasks
      .filter((task) => task.assignee_kind === 'position' && task.assignee_id)
      .map((task) => ({
        label: task.assignee_id as string,
        value: task.assignee_id as string,
      }));
    if (titles.length === 0) return directory;
    return { ...directory, positions: [...titles, ...directory.positions] };
  }, [directory, tasks]);
  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );
  const rows = useMemo(
    () =>
      tasks.map((task) =>
        planTaskToActionEntity(
          task,
          task.aor_id ? aorNameById.get(task.aor_id) : null
        )
      ),
    [aorNameById, tasks]
  );
  const taskById = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks]
  );

  const columns = useMemo<ColumnDef<ActionEntity, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Task',
        enableSorting: true,
        cell: ({ row }) => (
          <strong className="tw:text-foreground">{row.original.name}</strong>
        ),
        meta: { cellTitle: 'Task', headerTitle: 'Task' },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => (
          <StatusCell
            action={row.original}
            onChange={() => undefined}
            disabled
          />
        ),
        meta: { customSize: true, cellTitle: 'Status', headerTitle: 'Status' },
        size: 140,
      },
      {
        id: 'priority',
        header: 'Priority',
        enableSorting: true,
        accessorFn: (action) => actionPriorityRank(action.priority),
        cell: ({ row }) => (
          <PriorityCell
            action={row.original}
            onChange={() => undefined}
            disabled
          />
        ),
        meta: {
          customSize: true,
          cellTitle: 'Priority',
          headerTitle: 'Priority',
        },
        size: 110,
      },
      {
        id: 'assignee',
        header: 'Assignee',
        enableSorting: true,
        cell: ({ row }) => (
          <AssigneeCell
            action={row.original}
            directory={assigneeDirectory}
            onChange={() => undefined}
            disabled
          />
        ),
        meta: {
          customSize: true,
          cellTitle: 'Assignee',
          headerTitle: 'Assignee',
        },
        size: 170,
      },
      {
        id: 'target_date',
        accessorKey: 'target_date',
        header: 'Due',
        enableSorting: true,
        cell: ({ row }) => (
          <DueDateCell
            action={row.original}
            field="target_date"
            onChange={() => undefined}
            disabled
          />
        ),
        meta: { customSize: true, cellTitle: 'Due', headerTitle: 'Due' },
        size: 150,
      },
      {
        id: 'location',
        header: 'Location',
        enableSorting: true,
        accessorFn: (action) => actionLocationLabel(action),
        cell: ({ row }) => (
          <span className="tw:text-foreground">
            {actionLocationLabel(row.original)}
          </span>
        ),
        meta: { cellTitle: 'Location', headerTitle: 'Location' },
        size: 180,
      },
      {
        id: 'row_menu',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const task = taskById.get(String(row.original.id));
          if (!task) return null;
          return (
            <ActionRowMenu
              canEdit
              onEdit={() => onEdit(task)}
              onDelete={() => onDelete(task.id)}
            />
          );
        },
        meta: {
          customSize: true,
          align: 'center',
          headerAlign: 'center',
          cellTitle: 'Actions',
          headerTitle: 'Actions',
        },
        size: 50,
      },
    ],
    [assigneeDirectory, onDelete, onEdit, taskById]
  );

  return (
    <PratusTable
      columns={columns}
      data={rows}
      getRowId={(row) => String(row.id)}
      noDataIndication="No template tasks yet. Add the work this plan should create in a workspace."
      onRowClick={(row) => {
        const task = taskById.get(String(row.id));
        if (task) onEdit(task);
      }}
    />
  );
}
